/**
 * useSearch Hook
 *
 * @module search/hooks
 * @version 1.0
 *
 * @description
 * Hook React para búsqueda geoespacial.
 * Maneja estado, loading, errores y navegación automática.
 */

import { useState, useCallback } from 'react';
import { searchApiService } from '../services/search-api.service';
import { mapNavigatorService } from '../services/map-navigator.service';
import { MapEngineService } from '../../map-engine/MapEngineService';
import { useModuleStore } from '../../module-store';
import type { SearchHit, SearchRequest, SearchResponse, NavigationMode, SearchContext } from '../types';

/**
 * Estado del hook de búsqueda
 */
interface UseSearchState {
  query: string;
  results: SearchHit[];
  totalFound: number;
  isLoading: boolean;
  error: string | null;
  searchTime: number | null;
}

/**
 * Retorno del hook useSearch
 */
interface UseSearchReturn extends UseSearchState {
  search: (query: string, projectId: string) => Promise<void>;
  navigateToHit: (hit: SearchHit, mode?: NavigationMode) => Promise<void>;
  clearResults: () => void;
  setQuery: (query: string) => void;
}

/**
 * Obtiene el token JWT desde localStorage
 */
function getAuthToken(): string | null {
  const authStorage = localStorage.getItem('auth-storage');
  if (!authStorage) return null;

  try {
    const parsed = JSON.parse(authStorage);
    return parsed.state?.token || null;
  } catch (err) {
    console.warn('[useSearch] Error parsing auth-storage:', err);
    return null;
  }
}

/**
 * Hook principal de búsqueda
 */
export function useSearch(): UseSearchReturn {
  const [state, setState] = useState<UseSearchState>({
    query: '',
    results: [],
    totalFound: 0,
    isLoading: false,
    error: null,
    searchTime: null,
  });

  /**
   * Ejecuta búsqueda
   */
  const search = useCallback(async (query: string, projectId: string): Promise<void> => {
    // Validar query
    if (!query || query.trim().length < 2) {
      setState((prev) => ({
        ...prev,
        query,
        results: [],
        totalFound: 0,
        error: 'La búsqueda debe tener al menos 2 caracteres',
      }));
      return;
    }

    // Obtener token
    const token = getAuthToken();
    if (!token) {
      setState((prev) => ({
        ...prev,
        error: 'No autenticado. Por favor inicia sesión.',
      }));
      return;
    }

    // Loading state
    setState((prev) => ({
      ...prev,
      query,
      isLoading: true,
      error: null,
    }));

    try {
      // ── Contexto: módulo activo ──────────────────────────────
      // useModuleStore.getState() lee el store Zustand sin suscripción reactiva.
      // Es seguro dentro de un callback que no necesita re-ejecutarse por cambios de estado.
      const activeModule = useModuleStore.getState().activeModule ?? undefined;

      // ── Contexto: bounds y zoom del mapa ─────────────────────
      // MapEngineService es un singleton que mantiene la instancia de MapLibre.
      // getMap() puede devolver null si el mapa aún no se inicializó (caso de borde).
      const map = MapEngineService.getInstance().getMap();

      const context: SearchContext = { activeModule };

      if (map) {
        const b = map.getBounds();
        if (b) {
          context.mapBounds = {
            minLng: b.getWest(),
            minLat: b.getSouth(),
            maxLng: b.getEast(),
            maxLat: b.getNorth(),
          };
        }
        context.mapZoom = map.getZoom();
      }

      const request: SearchRequest = {
        query: query.trim(),
        projectId,
        maxResults: 10,
        context,
      };

      const response: SearchResponse = await searchApiService.search(request, token);

      setState((prev) => ({
        ...prev,
        results: response.hits,
        totalFound: response.totalFound,
        searchTime: response.searchTime,
        isLoading: false,
        error: null,
      }));

      console.log('[useSearch] Search completed:', {
        query,
        totalFound: response.totalFound,
        searchTime: response.searchTime,
        context,
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al buscar';
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
        results: [],
        totalFound: 0,
      }));

      console.error('[useSearch] Search error:', err);
    }
  }, []);

  /**
   * Navega a un resultado
   */
  const navigateToHit = useCallback(
    async (hit: SearchHit, mode: NavigationMode = 'select'): Promise<void> => {
      try {
        await mapNavigatorService.navigateToHit(hit, { mode });
      } catch (err) {
        console.error('[useSearch] Navigation error:', err);
      }
    },
    []
  );

  /**
   * Limpia los resultados
   */
  const clearResults = useCallback(() => {
    setState((prev) => ({
      ...prev,
      query: '',
      results: [],
      totalFound: 0,
      error: null,
      searchTime: null,
    }));
  }, []);

  /**
   * Actualiza el query
   */
  const setQuery = useCallback((query: string) => {
    setState((prev) => ({
      ...prev,
      query,
    }));
  }, []);

  return {
    ...state,
    search,
    navigateToHit,
    clearResults,
    setQuery,
  };
}
